// Vendor Libs
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {findIndex, get, remove, startCase} from 'lodash';

// Components
import AmsProgramSelect from './ams-program-select-overlay';
import Button from '../core/button';
import Checkbox from '../core/checkbox';
import Form from '../core/form';
import FormData from '../../containers/form-data';
import Input from '../core/input';
import Select from '../core/select';
import SubmitButton from '../core/submit-button';
import Thumbnail from '../core/thumbnail';
import ImageUploader from './image-uploader';

// Styles
import './display-path-form.scss';

function DisplayPathForm({ editing, formName, onReset, setFieldValue }) {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const getFieldProps = (fieldName, type) => ({
    className:
            type === 'select'
              ? 'display-path-form--select'
              : type === 'checkbox'
                  ? 'display-path-form--checkbox'
                  : 'display-path-form--input',
    label: fieldName === 'echostarId' ? `AMS ${startCase(fieldName)}` : startCase(fieldName),
    name: fieldName,
    type,
    visible:
            fieldName === 'adultLevel'
              ? shouldRenderAdultLevel
              : fieldName === 'echostarId'
                  ? shouldRenderEchostarIdInput
                  : true,
  });

  const resetForm = (event) => {
    event && event.preventDefault && event.preventDefault();

    onReset(false);
  };

  const setEchoId = (echoId) => {
    setFieldValue('echostarId', echoId);
  };

  const shouldRenderAdultLevel = (formData) => get(formData, 'adult') && get(formData, 'adult') !== 'false';

  const shouldRenderEchostarIdInput = (formData) => get(formData, 'echostarId') && get(formData, 'echostarId') !== '';

  const getDisplayPathImageCategory = (formData) => get(formData, 'values.displayPathImageCategory', '');

  const getImages = (formData) => get(formData, 'values.images', []);

  const getCurrentImageIndex = (formData) => findIndex(getImages(formData), (image) => image.category === getDisplayPathImageCategory(formData));

  const getImageUrl = (formData) => (getCurrentImageIndex(formData) !== -1
            ? getImages(formData)[getCurrentImageIndex(formData)].finalResourceName
            : '');

  const getCurrentDisplaypathImage = (formData) => (getImageUrl(formData) === '' ? null : <Thumbnail key="" src={getImageUrl(formData)} />);

  const getModifiedDisplayPathCategory = (formData) => {
    const displayPathCategory = getDisplayPathImageCategory(formData);
    return displayPathCategory === 'Franchise'
      ? 'Franchise 4:3'
      : displayPathCategory === 'SeriesBackground16x9'
        ? 'Background (Hero) 16:9'
              : displayPathCategory === 'Series16x9'
                ? 'Widescreen 16:9'
          : startCase(displayPathCategory);
  };

  const removeCurrentImage = (formData) => {
    const imagesMod = remove(
      Object.assign([], getImages(formData)),
      (image) => image.category !== getDisplayPathImageCategory(formData),
    );
    setFieldValue('images', imagesMod);
  };

  const handleUploaderChange = (formData, imageUrl) => {
    const category = getDisplayPathImageCategory(formData);
    const imagesMod = remove(Object.assign([], getImages(formData)), (image) => image.category !== category);

    imagesMod.push({
      category: category,
      finalResourceName: imageUrl || '',
      state: 'UPLOADED_REMOTE',
    });
    setFieldValue('images', imagesMod);
  };

  const formProps = {
    className: 'display-path-form',
    editing: !!editing,
    fixedPositionedResponse: true,
    name: formName,
    resetOnSubmit: formName === 'createDisplayPath',
    submitData: true,
  };

  return (
        <>
            {isOverlayOpen ? (
                <AmsProgramSelect
                    label="Choose Ams Program to Map"
                    setEchoId={(echoId) => setEchoId(echoId)}
                    onReset={() => setIsOverlayOpen(false)}
                />
            ) : null}
            <Form {...formProps}>
                <Input {...getFieldProps('displayPath')} disableAutofill={true} validate={['required']} />
                <Select
                    {...getFieldProps('status', 'select')}
                    validate={['required']}
                    onChange={(e) => { setFieldValue('status', e.target.value); }}
                >
                    <option value="">Choose the Status</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="UNKNOWN">Unknown</option>
                </Select>
                <Checkbox {...getFieldProps('adult', 'checkbox')} uncheckedValue="false" value="true" />
                <Input {...getFieldProps('adultLevel')} disableAutofill={true} validate={['required', 'number']} />
                <Select {...getFieldProps('displayPathImageCategory', 'select')}>
                    <option value="">Choose Display Path Image</option>
                    <option value="Franchise">Franchise 4:3</option>
                    <option value="Series16x9">Widescreen 16:9</option>
                    <option value="SeriesBackground16x9">Background (Hero) 16:9</option>
                    <option value="TitleArt">Title Art</option>
                </Select>
                <FormData formName={formName}>
                    {({ formData }) => (
                        <>
                            {getDisplayPathImageCategory(formData) === '' ? null : (
                                <div>
                                    <ImageUploader
                                        autoUpload={false}
                                        currentImage={getCurrentDisplaypathImage(formData)}
                                        formData={formData}
                                        onChange={(imageUrl) => handleUploaderChange(formData, imageUrl)}
                                    />
                                    {getCurrentImageIndex(formData) !== -1 ? (
                                        <>
                                            <h5 className="display-path-form--image-url-label">
                                                {getModifiedDisplayPathCategory(formData)} Image URL
                                            </h5>
                                            <p className="display-path-form--image-url">
                                                {getImages(formData)[getCurrentImageIndex(formData)].finalResourceName}
                                            </p>
                                            <Button
                                                className="display-path-form--unmap-displaypath-image-button"
                                                onClick={() => removeCurrentImage(formData)}
                                            >
                                                Remove {getModifiedDisplayPathCategory(formData)} Image
                                            </Button>
                                        </>
                                    ) : null}
                                </div>
                            )}
                            <div className="display-path-form--ams">
                                <Input {...getFieldProps('echostarId')} disableAutofill={true} disabled={true} />
                                <Button
                                    className="display-path-form--ams-button"
                                    onClick={() => setIsOverlayOpen(true)}
                                >
                                    Map AMS program
                                </Button>
                                {shouldRenderEchostarIdInput(formData.values) ? (
                                    <Button
                                        className="display-path-form--ams-unmap-button"
                                        onClick={() => setEchoId('')}
                                    >
                                        Unmap AMS program
                                    </Button>
                                ) : null}
                            </div>
                        </>
                    )}
                </FormData>
                <div className="display-path-form--controls">
                    <SubmitButton label="Save" />
                    <Button
                        {...{
                          onClick: resetForm,
                          className: 'display-path-form--reset',
                        }}
                    >
                        Reset
                    </Button>
                </div>
            </Form>
        </>
  );
}

DisplayPathForm.propTypes = {
    // className: PropTypes.string,
    editing: PropTypes.bool,
    formName: PropTypes.string.isRequired,
    onReset: PropTypes.func,
    setFieldValue: PropTypes.func,
};

export default DisplayPathForm;
